import React from "react";
import {IconAch, IconAdd} from "@bounce/shared/consumer/bounce-ui";
import {CardDetails} from "@bounce/shared/enums";
import {Card, Colors, Container, FormControl, InputLabel, MenuItem, Select, Text} from "@bounce/shared/ui";

const MAX_METHODS_AS_LIST = 5;
const SECTION_TITLE = "Pay from";
const ADD_METHOD_LABEL = "Connect another bank";
const SELECT_LABEL = "Bank account";
const FALLBACK_BANK_NAME = "Bank account";
const RADIO_SIZE = 16;
const RADIO_DOT_SIZE = 8;

const getBankLabel = ({institution_name, last_4_digits}: CardDetails) =>
  `${institution_name ?? FALLBACK_BANK_NAME} ••••${last_4_digits}`;

const RadioMark = ({checked}: {checked: boolean}) => (
  <Container sx={{position: "relative", width: RADIO_SIZE, height: RADIO_SIZE, flexShrink: 0}}>
    <div
      style={{
        width: RADIO_SIZE,
        height: RADIO_SIZE,
        borderRadius: "50%",
        border: `1px solid ${checked ? Colors.primary._800 : Colors.grey._600}`,
      }}
    />
    {checked && (
      <div
        style={{
          position: "absolute",
          left: (RADIO_SIZE - RADIO_DOT_SIZE) / 2,
          top: (RADIO_SIZE - RADIO_DOT_SIZE) / 2,
          width: RADIO_DOT_SIZE,
          height: RADIO_DOT_SIZE,
          borderRadius: "50%",
          background: Colors.primary._800,
        }}
      />
    )}
  </Container>
);

interface BankOptionProps {
  method: CardDetails;
  checked: boolean;
  showRadio: boolean;
  onClick: () => void;
}

const BankOption = ({method, checked, showRadio, onClick}: BankOptionProps) => (
  <Card
    sx={{
      padding: "16px",
      borderRadius: "16px",
      border: `${checked ? "2px" : "1px"} solid ${checked ? Colors.primary._800 : Colors.tertiary._300}`,
    }}
    onClick={onClick}
  >
    <Container row sx={{gap: "8px", alignItems: "center", width: "100%"}}>
      {showRadio && <RadioMark checked={checked} />}
      <IconAch />
      <Text variant="inter600">{getBankLabel(method)}</Text>
    </Container>
  </Card>
);

interface AchMethodPickerProps {
  methods: CardDetails[];
  selectedCardId: string | number | undefined;
  onAddPaymentMethod: () => void;
  setSelectedCardId: (id: string | number | undefined) => void;
}

const AchMethodPicker = ({methods, selectedCardId, onAddPaymentMethod, setSelectedCardId}: AchMethodPickerProps) => {
  const asDropdown = methods.length > MAX_METHODS_AS_LIST;

  return (
    <Container fullWidth sx={{alignItems: "start", gap: "8px"}}>
      <Text variant="inter600" size={16}>
        {SECTION_TITLE}
      </Text>

      {asDropdown ? (
        <FormControl variant="outlined" sx={{width: "100%"}}>
          <InputLabel>{SELECT_LABEL}</InputLabel>
          <Select
            fullWidth
            value={selectedCardId ?? ""}
            label={SELECT_LABEL}
            onChange={(event) => setSelectedCardId(event.target.value as string | number)}
          >
            {methods.map((method) => (
              <MenuItem key={method.id} value={method.id}>
                <Container row sx={{gap: "8px", alignItems: "center"}}>
                  <IconAch />
                  <Text variant="inter600">{getBankLabel(method)}</Text>
                </Container>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        methods.map((method) => (
          <BankOption
            key={method.id}
            method={method}
            checked={Number(selectedCardId) === method.id}
            showRadio={methods.length > 1}
            onClick={() => setSelectedCardId(method.id)}
          />
        ))
      )}

      <Card
        sx={{
          padding: "16px",
          borderRadius: "16px",
          border: `1px dashed ${Colors.primary._800}`,
          cursor: "pointer",
        }}
        onClick={onAddPaymentMethod}
      >
        <Container row sx={{gap: "8px", alignItems: "center", justifyContent: "center", width: "100%"}}>
          <Container sx={{color: Colors.primary._800}}>
            <IconAdd />
          </Container>
          <Text variant="inter600" color={Colors.primary._800}>
            {ADD_METHOD_LABEL}
          </Text>
        </Container>
      </Card>
    </Container>
  );
};

export default AchMethodPicker;
